import { ReactiveSet } from "@solid-primitives/set";
import { Message } from "../classes/Message.js";
import { hydrate } from "../hydration/index.js";
import { ClassCollection } from "./Collection.js";
/**
 * Collection of Messages
 */
export class MessageCollection extends ClassCollection {
    /**
     * Fetch message by Id
     * @param channelId Channel Id
     * @param messageId Message Id
     * @returns Message
     */
    async fetch(channelId, messageId) {
        const message = this.get(messageId);
        if (message && !this.isPartial(messageId))
            return message;
        const data = await this.client.api.get(`/channels/${channelId}/messages/${messageId}`);
        return this.getOrCreate(data._id, data, false);
    }
    /**
     * Get or create
     * @param id Id
     * @param data Data
     * @param isNew Whether this object is new
     */
    getOrCreate(id, data, isNew = false) {
        if (this.has(id) && !this.isPartial(id)) {
            // Merge fresh API data into the existing instance's underlying object.
            // Skip the `reactions` ReactiveMap — replacing it (or any of its inner
            // ReactiveSets) creates new identities which can cascade through any
            // component reading per-emoji subscribers (e.g. message reaction bar
            // re-renders) and contributed to the recursive Solid stack that
            // crashed browser_dm_group_cycle in the prior bulk-sweep attempt
            // (2026-05-22; see /tmp/collection-merge-sweep-status.md). Sync
            // reaction membership in-place on the existing ReactiveMap below.
            const fresh = hydrate("message", data, this.client, false);
            const { reactions: freshReactions, ...scalarFields } = fresh;
            this.updateUnderlyingObject(id, scalarFields);
            const existing = this.getUnderlyingObject(id);
            if (existing.reactions && freshReactions) {
                // Remove emojis no longer present
                for (const emoji of [...existing.reactions.keys()]) {
                    if (!freshReactions.has(emoji)) {
                        existing.reactions.delete(emoji);
                    }
                }
                // Add/sync emojis present in fresh
                for (const [emoji, freshUsers] of freshReactions) {
                    const existingUsers = existing.reactions.get(emoji);
                    if (!existingUsers) {
                        existing.reactions.set(emoji, new ReactiveSet(freshUsers));
                    }
                    else {
                        const freshUserSet = new Set(freshUsers);
                        for (const userId of [...existingUsers]) {
                            if (!freshUserSet.has(userId))
                                existingUsers.delete(userId);
                        }
                        for (const userId of freshUserSet) {
                            if (!existingUsers.has(userId))
                                existingUsers.add(userId);
                        }
                    }
                }
            }
            return this.get(id);
        }
        else {
            const instance = new Message(this, id);
            this.create(id, "message", instance, this.client, data);
            if (isNew)
                this.client.emit("messageCreate", instance);
            return instance;
        }
    }
    /**
     * Get or return partial
     * @param id Id
     */
    getOrPartial(id) {
        if (this.has(id)) {
            return this.get(id);
        }
        else if (this.client.options.partials) {
            const instance = new Message(this, id);
            this.create(id, "message", instance, this.client, {
                id,
                partial: true,
            });
            return instance;
        }
    }
}
